import PropTypes from 'prop-types'
import {Tooltip} from '@mui/material'
import RemoveIcon from '@mui/icons-material/Remove'
import {
  HeaderContainer,
  LabelContainer,
  LabelTypography,
  StyledIconButton,
  StyledInfoIcon,
  RemoveIconButton
} from './FilterHeaderStyles'

/**
 * FilterHeader Component
 *
 * Reusable header for filter components with label and optional remove button.
 * Can be used across range filters, checkbox filters, toggle filters, etc.
 *
 * @param {Object} props
 * @param {string} props.label - Label text to display
 * @param {string} props.description - Description to show in tooltip (optional)
 * @param {Function} props.onRemove - Callback when remove button is clicked
 * @param {boolean} props.showRemoveButton - Whether to show the remove button (default: true)
 * @param {number} props.min - Minimum value (for aria-label)
 * @param {number} props.max - Maximum value (for aria-label)
 * @param {string} props.ariaLabel - Custom aria-label for accessibility
 * @param {React.ReactNode} props.endAdornment - Optional element to display instead of remove button
 */
const FilterHeader = ({
  label,
  description,
  onRemove,
  showRemoveButton = true,
  min,
  max,
  ariaLabel,
  endAdornment,
  ...rest
}) => {
  // Generate aria-label based on context
  const getAriaLabel = () => {
    if (ariaLabel) return ariaLabel
    if (min !== undefined && max !== undefined) {
      return `${label} filter, minimum ${min}, maximum ${max}`
    }
    return `${label} filter`
  }

  return (
    <HeaderContainer {...rest}>
      {/* Label with optional info icon */}
      {label && (
        <LabelContainer>
          <LabelTypography variant='subtitle1' tabIndex={0} aria-label={getAriaLabel()}>
            {label}
          </LabelTypography>
          {description && (
            <Tooltip title={description} placement='top'>
              <StyledIconButton size='small'>
                <StyledInfoIcon />
              </StyledIconButton>
            </Tooltip>
          )}
        </LabelContainer>
      )}

      {/* End Adornment or Remove Button */}
      {endAdornment ||
        (showRemoveButton && onRemove && (
          <Tooltip title='Remove' placement='top'>
            <RemoveIconButton aria-label={`Remove ${label} filter`} onClick={onRemove} size='small'>
              <RemoveIcon fontSize='small' />
            </RemoveIconButton>
          </Tooltip>
        ))}
    </HeaderContainer>
  )
}

FilterHeader.propTypes = {
  label: PropTypes.string.isRequired,
  description: PropTypes.string,
  onRemove: PropTypes.func,
  showRemoveButton: PropTypes.bool,
  min: PropTypes.number,
  max: PropTypes.number,
  ariaLabel: PropTypes.string,
  endAdornment: PropTypes.node
}

export default FilterHeader
